import {
  ApprovalMode,
  ApprovalStrategy,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ApproverCandidate } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  PrimaryButton,
  chakra,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import type { StageLocal } from '~/components/approval/stage'
import { ApprovalModeTag } from '~/components/common/ApprovalModeTag'
import { Modal } from '~/components/common/Modal'
import { convertAreaLabel } from '~/models/area'
import {
  type CellKey,
  makeNoSectionCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import type { CellLocal } from '~/pages/AdminApprovalFlow/models/reducer'

type Props = {
  isOpen: boolean
  onClose: () => void
  processLabel: string
  areas: readonly AreaCode[]
  businessProcess: number
  cells: ReadonlyMap<CellKey, CellLocal>
  candidates: ApproverCandidate[]
  /** 「編集はセルクリックから」を出さない (マトリクスが表示されていない入口。 AC-APF-057)。 */
  hideHint?: boolean
}

export const SectionViewModal = ({
  isOpen,
  onClose,
  processLabel,
  areas,
  businessProcess,
  cells,
  candidates,
  hideHint = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const candidateMap = new Map(
    candidates.map((c) => [c.memberId, c.displayName]),
  )

  return (
    <Modal
      footer={
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          gap="8px"
          width="100%"
        >
          {hideHint ? (
            <chakra.span marginRight="auto" />
          ) : (
            <chakra.span
              marginRight="auto"
              style={{
                fontSize: '13px',
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
                color: '#6b7280',
              }}
            >
              {t('page:adminApprovalFlow.sectionViewModal.editHint')}
            </chakra.span>
          )}
          {/* design の断面ビュー modal footer 「閉じる」は `.btn`(40px) = md。 */}
          <PrimaryButton onClick={onClose} size="md">
            {t('page:adminApprovalFlow.actions.close')}
          </PrimaryButton>
        </Flex>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="xl"
      title={t('page:adminApprovalFlow.sectionViewModal.title')}
    >
      <Box
        marginBottom="8px"
        style={{
          fontSize: '13px',
          // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
          color: '#6b7280',
        }}
      >
        {processLabel}
      </Box>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: fontSizes.md.value,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ surface-secondary)
                background: '#f3f4f6',
                fontWeight: 700,
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
                color: '#6b7280',
                padding: '8px 12px',
                textAlign: 'left',
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ border-low-emphasis)
                borderBottom: '1px solid #e5e7eb',
                whiteSpace: 'nowrap',
              }}
            >
              {t('page:adminApprovalFlow.sectionViewModal.areaHeader')}
            </th>
            <th
              style={{
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ surface-secondary)
                background: '#f3f4f6',
                fontWeight: 700,
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
                color: '#6b7280',
                padding: '8px 12px',
                textAlign: 'left',
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ border-low-emphasis)
                borderBottom: '1px solid #e5e7eb',
                whiteSpace: 'nowrap',
              }}
            >
              {t('page:adminApprovalFlow.sectionViewModal.modeHeader')}
            </th>
            <th
              style={{
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ surface-secondary)
                background: '#f3f4f6',
                fontWeight: 700,
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
                color: '#6b7280',
                padding: '8px 12px',
                textAlign: 'left',
                // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ border-low-emphasis)
                borderBottom: '1px solid #e5e7eb',
                whiteSpace: 'nowrap',
              }}
            >
              {t('page:adminApprovalFlow.sectionViewModal.stageHeader')}
            </th>
          </tr>
        </thead>
        <tbody>
          {areas.map((area) => {
            const key = makeNoSectionCellKey(businessProcess, area)
            const cell = cells.get(key)
            const mode = cell?.mode ?? ApprovalMode.UNSPECIFIED
            return (
              <tr key={area}>
                <td
                  style={{
                    padding: '8px 12px',
                    // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ border-low-emphasis)
                    borderBottom: '1px solid #e5e7eb',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '64px',
                  }}
                >
                  {convertAreaLabel(area)}
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ border-low-emphasis)
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <ApprovalModeTag mode={mode} />
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ border-low-emphasis)
                    borderBottom: '1px solid #e5e7eb',
                    verticalAlign: 'top',
                  }}
                >
                  {mode !== ApprovalMode.APPROVAL ? (
                    <span
                      style={{
                        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
                        color: '#6b7280',
                      }}
                    >
                      —
                    </span>
                  ) : cell?.stages === undefined || cell.stages.length === 0 ? (
                    <chakra.span
                      color="Text.negativeHigh"
                      style={{ fontSize: '12px', fontWeight: 500 }}
                    >
                      {t(
                        'page:adminApprovalFlow.sectionViewModal.approverUnset',
                      )}
                    </chakra.span>
                  ) : (
                    <Flex flexDirection="column" gap="8px">
                      {cell.stages.map((stage, i) => (
                        <StageView
                          key={i}
                          candidateMap={candidateMap}
                          idx={i}
                          stage={stage}
                          t={t}
                        />
                      ))}
                    </Flex>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Modal>
  )
}

type StageViewProps = {
  idx: number
  stage: StageLocal
  candidateMap: Map<string, string>
  t: ReturnType<typeof useTranslation>['t']
}

const StageView = ({
  idx,
  stage,
  candidateMap,
  t,
}: StageViewProps): React.ReactElement => {
  const strategyLabel =
    stage.strategy === ApprovalStrategy.ALL
      ? t('page:adminApprovalFlow.strategy.all')
      : t('page:adminApprovalFlow.strategy.any')

  return (
    <div>
      <Flex alignItems="baseline" gap="8px">
        <span
          style={{
            fontWeight: 700,
            // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-high-emphasis)
            color: '#111827',
            fontSize: '13px',
          }}
        >
          {t('page:adminApprovalFlow.sectionViewModal.stageNum', {
            num: idx + 1,
          })}
        </span>
        <span
          style={{
            // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
            color: '#6b7280',
            fontSize: '13px',
          }}
        >
          {strategyLabel}
        </span>
      </Flex>
      <Flex flexWrap="wrap" gap="4px" marginTop="4px">
        {stage.approverMemberIds.length === 0 ? (
          <chakra.span
            color="Text.negativeHigh"
            style={{ fontSize: '12px', fontWeight: 500 }}
          >
            {t('page:adminApprovalFlow.sectionViewModal.approverUnset')}
          </chakra.span>
        ) : (
          stage.approverMemberIds.map((id) => {
            const displayName = candidateMap.get(id)
            // 候補不在 (eauth 上で ACTIVE でない) の欠落承認者 (AC-APF-026d /
            // Design Note D4)。StageEditModal と同じ negative 表現・中立語
            // 「(無効)」に揃える (生の member ID (auth0|...) は表示しない)。
            const isMissing = displayName === undefined
            const label =
              displayName ??
              t('page:adminApprovalFlow.stageModal.approverInactiveLabel')
            return isMissing ? (
              <chakra.span
                key={id}
                alignItems="center"
                display="inline-flex"
                padding="4px 10px"
                color="Text.negativeHigh"
                background="Surface.negativeLow"
                borderRadius="full"
                style={{ fontSize: '13px', fontWeight: 500 }}
              >
                {label}
              </chakra.span>
            ) : (
              <chakra.span
                key={id}
                alignItems="center"
                display="inline-flex"
                padding="4px 10px"
                borderRadius="full"
                style={{
                  // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ surface-secondary)
                  background: '#f3f4f6',
                  fontSize: '13px',
                  // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair ≈ text-mid-emphasis)
                  color: '#6b7280',
                }}
              >
                {label}
              </chakra.span>
            )
          })
        )}
      </Flex>
      {/* 欠落承認者を含む段階には StageEditModal の approverMissing と同じ
          再設定誘導文言を出す (D2: 警告のみ、保存・完了はブロックしない)。 */}
      {stage.approverMemberIds.some((id) => !candidateMap.has(id)) && (
        <Box
          marginTop="4px"
          color="Text.negativeHigh"
          style={{ fontSize: '12px', fontWeight: 500 }}
        >
          {t('page:adminApprovalFlow.stageModal.approverMissing')}
        </Box>
      )}
    </div>
  )
}
